import { useState } from "react";
import { AdminLayout } from "../components/AdminLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Package,
  Search,
  Plus,
  Tag,
  Layers,
  TrendingUp
} from "lucide-react";

interface AdminCatalogProps {
  onNavigate: (page: string) => void;
}

/**
 * Admin Catalog Page
 * Manage products and batches
 */
export function AdminCatalog({ onNavigate }: AdminCatalogProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("products");

  // Mock data
  const products = [
    {
      id: "1",
      sku: "ADV-20-C10-T5-F",
      brand: "Adven",
      name: "EMT-2 (Ghost Train Haze)",
      form: "flower",
      thc: 20,
      cbd: "<1",
      packSize: "10g",
      active: true,
      reviewCount: 45,
      avgRating: 4.5,
      batchCount: 3,
    },
    {
      id: "2",
      sku: "NOID-10-C20-T0-O",
      brand: "Noidecs",
      name: "CBD Oil 20%",
      form: "oil",
      thc: 0,
      cbd: 20,
      packSize: "30ml",
      active: true,
      reviewCount: 32,
      avgRating: 4.2,
      batchCount: 2,
    },
    {
      id: "3",
      sku: "KHIR-18-C1-T10-F",
      brand: "Khiron",
      name: "Hindu Kush",
      form: "flower",
      thc: 18,
      cbd: "<1",
      packSize: "10g",
      active: true,
      reviewCount: 28,
      avgRating: 4.7,
      batchCount: 4,
    },
  ];

  const batches = [
    {
      id: "1",
      batchNo: "ADV-20231015-001",
      productName: "ADV EMT-2 (Ghost Train Haze)",
      expiryDate: "2025-10-15",
      status: "in_stock",
      receivedAt: "2024-01-15",
      coaUrl: "https://example.com/coa/001.pdf",
    },
    {
      id: "2",
      batchNo: "NOID-20231201-042",
      productName: "Noidecs CBD Oil 20%",
      expiryDate: "2026-12-01",
      status: "in_stock",
      receivedAt: "2024-02-01",
      coaUrl: "https://example.com/coa/042.pdf",
    },
    {
      id: "3",
      batchNo: "KHIR-20230920-123",
      productName: "Khiron Hindu Kush",
      expiryDate: "2024-09-20",
      status: "out_of_stock",
      receivedAt: "2023-10-20",
      coaUrl: "https://example.com/coa/123.pdf",
    },
  ];

  const stats = {
    products: {
      total: products.length,
      active: products.filter(p => p.active).length,
      totalReviews: products.reduce((sum, p) => sum + p.reviewCount, 0),
    },
    batches: {
      total: batches.length,
      inStock: batches.filter(b => b.status === 'in_stock').length,
      outOfStock: batches.filter(b => b.status === 'out_of_stock').length,
    },
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'in_stock':
        return <Badge variant="default" className="bg-primary">In Stock</Badge>;
      case 'out_of_stock':
        return <Badge variant="destructive">Out of Stock</Badge>;
      case 'backorder':
        return <Badge variant="outline" className="border-amber-500 text-amber-700">Backorder</Badge>;
      case 'discontinued':
        return <Badge variant="secondary">Discontinued</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getFormBadge = (form: string) => {
    const colors: Record<string, string> = {
      flower: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
      oil: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
      capsule: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200",
    };
    return (
      <Badge variant="secondary" className={colors[form] || ""}>
        {form.charAt(0).toUpperCase() + form.slice(1)}
      </Badge>
    );
  };

  return (
    <AdminLayout currentPage="admin-catalog" onNavigate={onNavigate}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl gradient-green flex items-center justify-center">
              <Package className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl">Product Catalog</h1>
              <p className="text-muted-foreground">Manage products and batch tracking</p>
            </div>
          </div>
          <Button className="gap-2">
            <Plus className="w-4 h-4" />
            Add Product
          </Button>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="products">
              Products ({stats.products.total})
            </TabsTrigger>
            <TabsTrigger value="batches">
              Batches ({stats.batches.total})
            </TabsTrigger>
          </TabsList>

          {/* Products Tab */}
          <TabsContent value="products" className="space-y-6">
            {/* Stats */}
            <div className="grid sm:grid-cols-3 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Products</p>
                      <p className="text-2xl font-bold mt-1">{stats.products.total}</p>
                    </div>
                    <Package className="w-8 h-8 text-primary" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Active</p>
                      <p className="text-2xl font-bold mt-1">{stats.products.active}</p>
                    </div>
                    <Tag className="w-8 h-8 text-muted-foreground" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Reviews</p>
                      <p className="text-2xl font-bold mt-1">{stats.products.totalReviews}</p>
                    </div>
                    <TrendingUp className="w-8 h-8 text-muted-foreground" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Search */}
            <Card>
              <CardContent className="pt-6">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by SKU, brand, or name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Products List */}
            <div className="space-y-4">
              {products.map((product) => (
                <Card key={product.id}>
                  <CardContent className="pt-6">
                    <div className="space-y-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <code className="text-xs bg-muted px-2 py-0.5 rounded font-mono">
                              {product.sku}
                            </code>
                            {getFormBadge(product.form)}
                            {product.active && (
                              <Badge variant="default" className="bg-primary">Active</Badge>
                            )}
                          </div>
                          <h3 className="font-semibold text-lg">
                            {product.brand} - {product.name}
                          </h3>
                        </div>
                        <Button variant="outline" size="sm">Edit</Button>
                      </div>

                      <div className="grid sm:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground">THC</p>
                          <p className="font-semibold">{product.thc}%</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">CBD</p>
                          <p className="font-semibold">{product.cbd}%</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Pack Size</p>
                          <p className="font-semibold">{product.packSize}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Batches</p>
                          <p className="font-semibold">{product.batchCount}</p>
                        </div>
                      </div>

                      <div className="pt-2 border-t flex items-center justify-between">
                        <div className="flex items-center gap-4 text-sm">
                          <span className="text-muted-foreground">
                            <span className="font-semibold text-foreground">{product.reviewCount}</span> reviews
                          </span>
                          <span className="text-muted-foreground">
                            ⭐ <span className="font-semibold text-foreground">{product.avgRating}</span> avg rating
                          </span>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => onNavigate(`product-detail?id=${product.id}`)}
                        >
                          View Details
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Batches Tab */}
          <TabsContent value="batches" className="space-y-6">
            {/* Stats */}
            <div className="grid sm:grid-cols-3 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Batches</p>
                      <p className="text-2xl font-bold mt-1">{stats.batches.total}</p>
                    </div>
                    <Layers className="w-8 h-8 text-primary" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">In Stock</p>
                      <p className="text-2xl font-bold mt-1">{stats.batches.inStock}</p>
                    </div>
                    <Package className="w-8 h-8 text-primary" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Out of Stock</p>
                      <p className="text-2xl font-bold mt-1">{stats.batches.outOfStock}</p>
                    </div>
                    <Package className="w-8 h-8 text-destructive" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Batches List */}
            <div className="space-y-4">
              {batches.map((batch) => (
                <Card key={batch.id}>
                  <CardContent className="pt-6">
                    <div className="space-y-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <code className="text-sm font-mono font-semibold">{batch.batchNo}</code>
                          <p className="text-muted-foreground mt-1">{batch.productName}</p>
                        </div>
                        {getStatusBadge(batch.status)}
                      </div>

                      <div className="grid sm:grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground">Received</p>
                          <p className="font-medium">
                            {new Date(batch.receivedAt).toLocaleDateString('en-GB')}
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Expiry Date</p>
                          <p className="font-medium">
                            {new Date(batch.expiryDate).toLocaleDateString('en-GB')}
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">COA</p>
                          <a 
                            href={batch.coaUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="font-medium text-primary hover:underline"
                          >
                            View Certificate
                          </a>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
}
